import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Grid, Paper, Chip, Button,
  IconButton, Tooltip, TextField
} from "@mui/material";
import {
  Visibility as ViewIcon, FilterList as FilterIcon,
  FileDownload as ExportIcon, AccountBalance as PaymentIcon,
  Replay as RefundIcon, Receipt as ReimbIcon
} from "@mui/icons-material";
import { fetchData } from "../../BaseUrl";
import { exportToCSV } from "../../utils/tableUtils";
import Dynamictablepagination from "../../DynamicComponent/Dynamictablepagination";
import Toast from "../../DynamicComponent/Toast";
import PaymentDetailDialog from "./PaymentDetailDialog";
import WithdrawalDetailDialog from "./WithdrawalDetailDialog";
import ReimbursementDetailDialog from "./ReimbursementDetailDialog";
import BiliteracyDetailDialog from "./BiliteracyDetailDialog";
import RefundDetailDialog from "./RefundDetailDialog";

const TAB_LABELS = ["All", "Admission Payment", "Biliteracy Fees", "Re-Imbursement", "Withdrawal", "Refund"];

const AdminPayment = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dashboard, setDashboard] = useState({
    totalCollected: 52400, reimbursementCount: 12, reimbursementPending: 1656,
    refundCount: 8, refundPending: 1200, biliteracyFees: 1080
  });
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadDashboard = useCallback(async () => {
    try {
      const result = await fetchData("admin/payment/dashboard");
      if (result?.data) {
        const d = result.data;
        setDashboard({
          totalCollected: d.totalCollected || 0,
          reimbursementCount: d.reimbursements?.count || 0,
          reimbursementPending: d.reimbursements?.pendingAmount || 0,
          refundCount: d.refunds?.count || 0,
          refundPending: d.refunds?.pendingAmount || 0,
          biliteracyFees: d.biliteracyTotal || 0
        });
      }
    } catch (err) { /* silent */ }
  }, []);

  const loadRecords = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);

      let endpoint = "admin/payment/list";
      const tabTypes = ["all", "admission", "biliteracy", "reimbursement", "withdrawal", "refund"];
      const type = tabTypes[tabValue];

      if (tabValue === 2) endpoint = "admin/biliteracy-mgmt/list";
      else if (tabValue === 3) endpoint = "admin/payment/reimbursements";
      else if (tabValue === 5) endpoint = "admin/payment/refunds";
      else if (tabValue === 4) endpoint = "admin/payment/withdrawals";
      else if (type !== "all") params.append("type", type);

      const result = await fetchData(`${endpoint}?${params.toString()}`);
      if (result?.data) {
        const list =
          result.data.reimbursements ||
          result.data.refunds ||
          result.data.withdrawals ||
          result.data.records ||
          result.data.payments ||
          result.data ||
          [];
        setRecords(Array.isArray(list) ? list : []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load payment records" });
    }
  }, [tabValue, filters]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleActionComplete = (message) => {
    setToast({ open: true, severity: "success", message });
    setDetailOpen(false);
    loadRecords(pagination.page);
  };

  const handleExport = () => {
    if (!records.length) {
      setToast({ open: true, severity: "warning", message: "No records to export" });
      return;
    }
    const tabLabel = TAB_LABELS[tabValue].replace(/\s+/g, '_');
    const filename = `Payment_${tabLabel}_${new Date().toISOString().slice(0, 10)}.csv`;
    exportToCSV(getColumns(), records, filename);
    setToast({ open: true, severity: "success", message: "Export downloaded" });
  };

  const kpiCards = [
    { label: "Total Collected", value: `$${dashboard.totalCollected?.toLocaleString()}`, color: "#4caf50", bg: "#e8f5e9", icon: <PaymentIcon /> },
    { label: "Reimbursement", value: `${dashboard.reimbursementCount}`, sub: `$${dashboard.reimbursementPending} pending`, color: "#2196f3", bg: "#e3f2fd", icon: <ReimbIcon /> },
    { label: "Refund Request", value: `${dashboard.refundCount}`, sub: `$${dashboard.refundPending} pending`, color: "#ff9800", bg: "#fff3e0", icon: <RefundIcon /> },
    { label: "Biliteracy Fees", value: `$${dashboard.biliteracyFees?.toLocaleString()}`, color: "#336599", bg: "#f3e5f5", icon: <PaymentIcon /> }
  ];

  // Columns per tab
  const allColumns = [
    { label: "Student Name", field: "studentName" },
    { label: "Reg No", field: "regNo" },
    { label: "Grade", field: "grade" },
    { label: "Payment ID", field: "paymentId" },
    { label: "Payee", field: "payee" },
    { label: "Amount", field: "amount" },
    { label: "Date", field: "date" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "actions" }
  ];

  const biliteracyColumns = [
    { label: "Student Name", field: "studentName" },
    { label: "App No", field: "appNo" },
    { label: "Grade", field: "grade" },
    { label: "Enrolled", field: "enrolled" },
    { label: "Section", field: "section" },
    { label: "Evaluated Date", field: "evaluatedDate" },
    { label: "Registered For Nilai08", field: "registeredForNilai08Chip" },
    { label: "Seal Of Biliteracy", field: "sealOfBiliteracyChip" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "actions" }
  ];

  const reimbursementColumns = [
    { label: "Request ID", field: "requestId" },
    { label: "Teacher Name", field: "teacherName" },
    { label: "Expense Title", field: "expenseTitle" },
    { label: "Amount", field: "amount" },
    { label: "Date", field: "date" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "actions" }
  ];

  const refundColumns = [
    { label: "Reg No", field: "regNo" },
    { label: "Parent Name", field: "parentName" },
    { label: "Student Name", field: "studentName" },
    { label: "Grade", field: "grade" },
    { label: "Amount", field: "amount" },
    { label: "Reason Title", field: "reasonTitle" },
    { label: "Action", field: "actions" }
  ];

  const withdrawalColumns = [
    { label: "Student Name", field: "studentName" },
    { label: "Reg No", field: "regNo" },
    { label: "Grade", field: "grade" },
    { label: "Parent Name", field: "parentName" },
    { label: "Parent Ph", field: "parentPhone" },
    { label: "Parent Email ID", field: "parentEmail" },
    { label: "Date", field: "date" },
    { label: "Reason For Withdrawal", field: "reasonForWithdrawal" },
    { label: "Document", field: "document" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "actions" }
  ];

  const getColumns = () => {
    switch (tabValue) {
      case 2: return biliteracyColumns;
      case 3: return reimbursementColumns;
      case 4: return withdrawalColumns;
      case 5: return refundColumns;
      default: return allColumns;
    }
  };

  const statusColorMap = {
    verified: { color: "#4caf50", bg: "#e8f5e9" },
    paid: { color: "#4caf50", bg: "#e8f5e9" },
    pending: { color: "#ff9800", bg: "#fff3e0" },
    rejected: { color: "#f44336", bg: "#fce4ec" },
    approved: { color: "#4caf50", bg: "#e8f5e9" },
    processing: { color: "#2196f3", bg: "#e3f2fd" }
  };

  const yesNoChip = (val) => {
    const isYes = val === "yes" || val === true || val === "Yes";
    if (!val && val !== false) return "-";
    return (
      <Chip label={isYes ? "Yes" : "No"} size="small" sx={{
        backgroundColor: isYes ? "#e8f5e9" : "#fce4ec",
        color: isYes ? "#4caf50" : "#f44336",
        fontWeight: 600
      }} />
    );
  };

  const rows = records.map((r) => ({
    ...r,
    studentName: r.studentName || r.name || "-",
    regNo: r.registrationNumber || r.regNo || "-",
    grade: r.grade || "-",
    paymentId: r.paymentId || r.transactionId || "-",
    payee: r.payee || r.payeeName || "-",
    amount: r.amount ? `$${r.amount}` : "-",
    date: r.date || r.paymentDate || r.createdAt?.slice(0, 10) || "-",
    appNo: r.applicationNumber || r.appNo || "-",
    enrolled: r.enrolled || "-",
    section: r.section || "-",
    evaluatedDate: r.evaluatedDate || "-",
    requestId: r.requestId || r.id || "-",
    teacherName: r.teacherName || "-",
    expenseTitle: r.expenseTitle || r.title || "-",
    parentName: r.parentName || "-",
    parentPhone: r.parentPhone || "-",
    parentEmail: r.parentEmail || r.email || "-",
    reasonTitle: r.reasonTitle || r.reason || "-",
    reasonForWithdrawal: r.reasonForWithdrawal || r.reason || "-",
    registeredForNilai08Chip: yesNoChip(r.registeredForNilai08),
    sealOfBiliteracyChip: yesNoChip(r.sealOfBiliteracy),
    document: r.document ? (
      <Chip label="View Doc" size="small" clickable sx={{ backgroundColor: "#e3f2fd", color: "#2196f3" }} />
    ) : "-",
    statusChip: (
      <Chip label={r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : "Pending"}
        size="small" sx={{
          backgroundColor: (statusColorMap[r.status?.toLowerCase()] || statusColorMap.pending).bg,
          color: (statusColorMap[r.status?.toLowerCase()] || statusColorMap.pending).color,
          fontWeight: 600
        }} />
    ),
    actions: (
      <Tooltip title="View">
        <IconButton size="small" onClick={() => { setSelectedRecord(r); setDetailOpen(true); }}>
          <ViewIcon fontSize="small" sx={{ color: "#244C77" }} />
        </IconButton>
      </Tooltip>
    )
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Payment Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Payment Management</Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {kpiCards.map((kpi, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: kpi.bg, display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2, backgroundColor: kpi.color + "20",
                display: "flex", alignItems: "center", justifyContent: "center", color: kpi.color
              }}>
                {kpi.icon}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" color={kpi.color}>{kpi.value}</Typography>
                <Typography variant="body2" color="text.secondary">{kpi.label}</Typography>
                {kpi.sub && <Typography variant="caption" color="text.secondary">{kpi.sub}</Typography>}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filter Row */}
      <Grid container spacing={2} mb={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth placeholder="Search..."
            value={filters.search} onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadRecords()} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => loadRecords()}
            sx={{ height: 40 }}>Apply</Button>
        </Grid>
        <Grid item xs={12} sm={7} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}
            sx={{ textTransform: "none" }}>Export</Button>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        {TAB_LABELS.map((label) => <Tab key={label} label={label} />)}
      </Tabs>

      {/* Table */}
      <Dynamictablepagination columns={getColumns()} rows={rows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadRecords(newPage)} />

      {selectedRecord && tabValue === 2 && (
        <BiliteracyDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)}
          record={selectedRecord} onSuccess={handleActionComplete} />
      )}
      {selectedRecord && tabValue === 3 && (
        <ReimbursementDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)}
          record={selectedRecord} onSuccess={handleActionComplete} />
      )}
      {selectedRecord && tabValue === 4 && (
        <WithdrawalDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)}
          record={selectedRecord} onSuccess={handleActionComplete} />
      )}
      {selectedRecord && tabValue === 5 && (
        <RefundDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)}
          record={selectedRecord} onSuccess={handleActionComplete} />
      )}
      {selectedRecord && (tabValue === 0 || tabValue === 1) && (
        <PaymentDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)}
          record={selectedRecord} onSuccess={handleActionComplete} />
      )}

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminPayment;
